import { useState, useEffect } from "react";
import { products } from "@/mocks/productsCdbjjLP";

const PHG_LOGO = "/images/phg-logo.png";
const CDBJJ_LOGO = "/images/carpe2.png";

export default function ProductGridCdbjj() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-10");
          }
        });
      },
      { threshold: 0.06 }
    );
    document.querySelectorAll(".card-reveal-cdbjj").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="products" className="w-full bg-black py-24 px-4 md:px-8">
      <div className="max-w-6xl mx-auto mb-16 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-red-600 text-xs tracking-widest uppercase mb-3">Collection 2026</p>
          <h2 className="text-white text-4xl md:text-5xl font-black uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            4 ITEMS
          </h2>
          <p className="text-white/40 text-sm mt-2 tracking-wide">
            PHG × CDBJJ — Live the Discipline Active Collection
          </p>
        </div>
        <div className="flex items-center gap-4 border border-white/10 px-5 py-3">
          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
            <img src={PHG_LOGO} alt="Powerhouse Gym" className="w-full h-full object-contain" />
          </div>
          <span className="text-white/20 text-xs" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.3em" }}>×</span>
          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
            <img src={CDBJJ_LOGO} alt="Carpe Diem BJJ" className="w-full h-full object-contain" />
          </div>
          <div className="w-px h-6 bg-white/10" />
          <span className="text-white/30 text-xs tracking-widest uppercase whitespace-nowrap" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            Collab Collection
          </span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5" data-product-shop>
        {products.map((product, i) => (
          <div
            key={product.id}
            className="card-reveal-cdbjj opacity-0 translate-y-10 transition-all duration-700"
            style={{ transitionDelay: `${i * 120}ms` }}
          >
            <ProductCard product={product} index={i} />
          </div>
        ))}
      </div>
    </section>
  );
}

function ProductCard({ product, index }: { product: (typeof products)[0]; index: number }) {
  const [activeColor, setActiveColor] = useState(0);
  const hasColors = product.colors && product.colors.length > 0;
  const displayImage = hasColors ? product.colors![activeColor].image : product.image;

  return (
    <a href={`#product-${product.slug}`} className="group bg-[#0a0a0a] flex flex-col cursor-pointer overflow-hidden block">
      <div className="relative w-full overflow-hidden bg-[#111]" style={{ height: "400px" }}>
        <img src={displayImage} alt={product.name} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-400" />
        <div className="absolute bottom-2 right-3 text-white/6 font-black select-none pointer-events-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "80px", lineHeight: 1 }}>
          {String(index + 1).padStart(2, "0")}
        </div>
        <div className="absolute top-3 left-3 bg-black/70 text-red-500 text-[10px] tracking-widest uppercase px-2 py-1 border border-red-600/40">
          {product.category}
        </div>
        {hasColors && (
          <div className="absolute top-3 right-3 flex gap-1">
            {product.colors!.map((c, ci) => (
              <button
                key={c.label}
                onClick={(e) => { e.preventDefault(); setActiveColor(ci); }}
                className={`text-[9px] tracking-widest px-2 py-0.5 border transition-all duration-200 whitespace-nowrap cursor-pointer ${activeColor === ci ? "bg-white text-black border-white" : "bg-black/60 text-white/50 border-white/20 hover:border-white/50"}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <span className="text-white text-xs tracking-[0.3em] uppercase border border-white/50 px-5 py-2 bg-black/40">詳細を見る</span>
        </div>
      </div>
      <div className="p-6 flex flex-col gap-3 flex-1 border-t border-white/10">
        <p className="text-white/55 text-xs tracking-widest uppercase">{product.id}</p>
        <h3 className="text-white font-bold leading-snug" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em", fontSize: "22px" }}>
          {product.name}
        </h3>
        <p className="text-white/70 text-sm leading-relaxed line-clamp-2">{product.tagline}</p>
        {hasColors && (
          <div className="flex items-center gap-2">
            <span className="text-white/55 text-xs tracking-widest uppercase">Color:</span>
            <span className="text-white/80 text-xs tracking-widest uppercase">{product.colors![activeColor].label}</span>
          </div>
        )}
        <div className="flex flex-wrap gap-1.5 mt-1">
          {product.features?.slice(0, 2).map((f) => (
            <span key={f} className="text-xs text-white/60 border border-white/20 px-2 py-0.5 tracking-wide">{f}</span>
          ))}
        </div>
        <div className="flex items-center justify-between mt-auto pt-3 border-t border-white/10">
          <span className="text-white font-bold text-xl">
            ¥{Math.floor(product.price * 1.1).toLocaleString()}
            <span className="text-white/55 text-sm ml-1">税込</span>
          </span>
          <span className="text-red-400 text-sm tracking-wider uppercase flex items-center gap-1 group-hover:text-red-300 transition-colors whitespace-nowrap">
            詳細 <i className="ri-arrow-right-s-line" />
          </span>
        </div>
      </div>
    </a>
  );
}
